import {
  Flex,
  Table,
  Progress,
  Icon,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Switch,
  IconButton,
  Button,
  Skeleton,
  styled,
  Tooltip,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

// Custom components
import Card from "components/card/Card";
import Menu from "components/menu/MainMenu";
import { useSourceApps } from "hooks/useSourceApps";

// Assets
import { MdCheckCircle, MdCancel, MdOutlineError } from "react-icons/md";
import { FaAndroid, FaApple } from "react-icons/fa";
import { EditIcon } from "@chakra-ui/icons";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getFirstDayOfWeek } from "utils";

export default function ColumnsTable(props: {
  columns: {
    Header: string;
    accessor: string;
    hint?: string;
  }[];
  tableData: any;
  isLoading: boolean;
  lastCompletedPeriod?: number;
}) {
  const dispatch = useDispatch();
  const history = useHistory();

  const { columns, tableData, isLoading } = props;

  const { namesByAppId } = useSourceApps();

  const [tableLoading, setTableLoading] = useState(isLoading);
  const [loadingStartTime, setLoadingStartTime] = useState<number | null>(null);

  const tableInstance = useTable(
    {
      columns: columns,
      data: tableData,
      initialState: { pageSize: 200 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    rows,
    rowsById,
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    initialState,
  } = tableInstance;
  initialState.pageSize = 200;

  const days = useMemo(
    () => tableData.map((itm: any) => itm.days),
    [tableData]
  );

  const highestDayValues = useMemo(() => {
    let values: { [keys: string]: any } = {};

    days.forEach((day: any) => {
      const keys = Object.keys(day);
      keys.forEach((key: string) => {
        const existingDayValue = values?.[key];
        const retentionValue = day[key].retention;
        if (!existingDayValue) {
          values[key] = retentionValue;
          return;
        }

        if (existingDayValue < retentionValue) {
          values[key] = retentionValue;
          return;
        }
      });
    });

    return values;
  }, [days]);

  useEffect(() => {
    tableInstance.setPageSize(200);
  }, [tableData, columns, tableLoading]);

  useEffect(() => {
    if (isLoading && !tableLoading) {
      setLoadingStartTime(Date.now());
      setTableLoading(true);
      return;
    }

    if (isLoading) return;

    const timeDifference = Date.now() - loadingStartTime;

    if (timeDifference > 1000) {
      setTableLoading(false);
      return;
    }

    const timeout = setTimeout(() => {
      setTableLoading(false);
    }, 1000 - timeDifference);

    return () => clearTimeout(timeout);
  }, [isLoading, tableLoading, loadingStartTime]);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  return (
    <Card
      flexDirection="column"
      w="100%"
      mt={10}
      overflowX={{ sm: "scroll", lg: "scroll" }}
      overflowY="scroll"
      maxH="calc(100vh - 200px)"
      p={"0px"}
      px="20px"
      pb={"20px"}
    >
      <Flex
        px="25px"
        justify="space-between"
        mb="10px"
        mt="20px"
        align="center"
      >
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Events
        </Text>
      </Flex>
      <Table variant="striped" color="gray.800" mb="12px">
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => {
                return (
                  <Th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    px={"4px"}
                    key={index}
                    borderColor={borderColor}
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 0,
                      backgroundColor: "white",
                    }}
                  >
                    {
                      //@ts-ignore
                      column.hint ? (
                        //@ts-ignore
                        <Tooltip label={column.hint}>
                          <Flex
                            justify="space-between"
                            align="center"
                            fontSize={{ sm: "14px", lg: "12px" }}
                            color="gray.400"
                            textAlign={"center"}
                            justifyContent="center"
                          >
                            {column.render("Header")}
                          </Flex>
                        </Tooltip>
                      ) : (
                        <Flex
                          justify="space-between"
                          align="center"
                          fontSize={{ sm: "14px", lg: "12px" }}
                          color="gray.400"
                          textAlign={"center"}
                          justifyContent="center"
                        >
                          {column.render("Header")}
                        </Flex>
                      )
                    }
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {tableLoading &&
            Array.from({ length: 50 }).map((_, index) => {
              return (
                <Tr>
                  {columns.map((column, index) => (
                    <Td
                      key={index}
                      fontSize={{ sm: "14px" }}
                      maxH="30px !important"
                      px={"8px"}
                      py={"8px"}
                      minW={{ sm: "80px", md: "120px", lg: "2px" }}
                      borderColor="transparent"
                    >
                      <Skeleton
                        borderRadius={8}
                        endColor={"rgba(53,63,255,0.17)"}
                        height={8}
                      />
                    </Td>
                  ))}
                </Tr>
              );
            })}
          {!tableLoading && page.length === 0 && (
            <Tr>
              <Td colSpan={columns.length} textAlign="center">
                <Text fontSize="sm" color="gray.400">
                  No data available
                </Text>
              </Td>
            </Tr>
          )}
          {!tableLoading &&
            page.map((row, index) => {
              prepareRow(row);

              //const isSummaryRow = row.values.date.includes('9999-01-01');
              const isSummaryRow = row.values.date?.includes("9999-01-01");

              return (
                <Tr {...row.getRowProps()} key={index}>
                  {row.cells.map((cell, index) => {
                    //@ts-ignore
                    const isBreakdown = cell.column.isBreakdown;

                    let data;

                    if (cell.column.Header === "NAME") {
                      data = (
                        <Text
                          _hover={{ textDecoration: "underline" }}
                          color={textColor}
                          fontSize="sm"
                          fontWeight="700"
                          textAlign={"center"}
                        >
                          {cell.value}
                        </Text>
                      );
                    } else if (cell.column.Header === "Date Range") {
                      //@ts-ignore
                      const periodicity = cell.row.original.periodicity;

                      data = (
                        <Text
                          _hover={{ textDecoration: "underline" }}
                          color={textColor}
                          fontSize="xs"
                          fontWeight="600"
                          textAlign={"center"}
                          whiteSpace="pre-wrap"
                        >
                          {periodicity.type === "daily"
                            ? periodicity.day
                            : periodicity.type === "weekly"
                            ? `Week ${
                                periodicity.week
                              }${"\n"}${getFirstDayOfWeek(
                                periodicity.year,
                                periodicity.week
                              )}`
                            : periodicity.type === "monthly"
                            ? `${periodicity.month} - ${periodicity.year}`
                            : "Total"}
                        </Text>
                      );
                    } else if (cell.column.Header === "ACTIVE") {
                      data =
                        //@ts-ignore
                        cell.row.original.isDirect ? (
                          <Switch
                            onChange={() => {
                              dispatch(
                                //@ts-ignore
                                UpdateCampaignStatusAction(
                                  //@ts-ignore
                                  cell.row.original.id,
                                  !cell.value
                                )
                              );
                            }}
                            style={{ marginRight: -80 }}
                            isChecked={cell.value}
                            size="sm"
                          />
                        ) : null;
                    } else if (cell.column.Header === "APP") {
                      data = (
                        <img
                          referrerPolicy="no-referrer"
                          style={{
                            width: 40,
                            height: 40,
                            minWidth: 40,
                            borderRadius: 4,
                          }}
                          src={cell.value}
                        />
                      );
                    } else if (cell.column.Header === "PROGRESS") {
                      data = (
                        <Flex align="center">
                          <Progress
                            variant="table"
                            colorScheme="brandScheme"
                            h="8px"
                            w="108px"
                            value={cell.value}
                          />
                        </Flex>
                      );
                    } else if (
                      (cell.column.Header.toString().includes("D") ||
                        cell.column.Header.toString() == "To Date") &&
                      cell.column.Header.toString() != "DATE" &&
                      cell.column.Header.toString() != "SPEND"
                    ) {
                      const cellValueIsMultiple = Array.isArray(cell.value);

                      const cellValue = cellValueIsMultiple
                        ? cell.value[0]
                        : cell.value;

                      const coinsEarned = cellValueIsMultiple
                        ? cell.value[1]
                        : undefined;

                      const dx = extractNumberFromString(
                        cell.column.Header.toString()
                      );

                      const percentageToD0 =
                        cellValue / highestDayValues[`D${dx}`];

                      const isDayFinished = isDateDifferenceLessThanOrEqual(
                        cell.row.values.date,
                        dx
                      );

                      let percentage = (cellValue * 100).toFixed(2);

                      if (
                        !isDayFinished ||
                        cell.row.values.date?.includes("9999-01-01")
                      ) {
                        data = (
                          <Flex
                            px={"4px"}
                            py={"6px"}
                            display="flex"
                            flexDirection="column"
                            style={{
                              minWidth: 44,
                              minHeight: 44,
                              borderRadius: 4,
                              backgroundColor: `rgb(40, 184, 255, ${percentageToD0})`,
                              opacity: !isDayFinished ? 1 : 0.5,
                              justifyContent: "center",
                              alignItems: "center",
                              display: "flex",
                              borderStyle: "dotted",
                              //@ts-ignore
                              borderWidth: cell.row.original.days?.[
                                cell.column.Header.toString()
                              ]?.isOngoing
                                ? 4
                                : 0,
                            }}
                          >
                            <Text
                              textShadow={"0.1px 0.1px black"}
                              color={textColor}
                              textAlign={"center"}
                              fontSize="xs"
                              fontWeight="600"
                              overflow={"clip"}
                            >
                              {!isNaN(cellValue)
                                ? cellValue >= 1 && cellValue == 0
                                  ? "<1%"
                                  : `${percentage}%`
                                : "-"}
                            </Text>
                            {coinsEarned !== undefined ? (
                              <Tooltip label="Coin Factor">
                                {(coinsEarned / 5200).toFixed(0)}
                              </Tooltip>
                            ) : (
                              <></>
                            )}
                          </Flex>
                        );
                      } else {
                        data = (
                          <div
                            style={{
                              width: 62,
                              height: 42,
                              borderRadius: 4,
                              //backgroundColor: `rgb(40, 184, 255, ${percentageToD0})`,
                              opacity: !isDayFinished ? 1 : 0.5,
                              padding: 4,
                              justifyContent: "center",
                            }}
                          ></div>
                        );
                      }
                    } else if (
                      cell.column.Header.toString() == "DATE" &&
                      cell.value.includes("9999-01-01")
                    ) {
                      data = (
                        <Text
                          color={"black"}
                          fontSize="l"
                          fontWeight="700"
                          textAlign={"center"}
                        ></Text>
                      );
                    } else if (cell.column.Header.toString() == "DATE") {
                      data = (
                        <Text
                          color={"black"}
                          fontSize="l"
                          fontWeight="700"
                          textAlign={"center"}
                        >
                          {cell.value.substring(0, 10)}
                        </Text>
                      );
                    } else if (cell.column.Header === "ID") {
                      data = (
                        <IconButton
                          aria-label={"edit"}
                          onClick={() => {
                            history.push("/ua/cpi/create?id=" + cell.value);
                          }}
                          icon={<EditIcon />}
                        />
                      );
                    } else if (cell.column.Header === "COUNTRIES") {
                      data = (
                        <Text
                          color={textColor}
                          fontSize="sm"
                          fontWeight="600"
                          textAlign={"center"}
                        >
                          {cell.value ? cell.value.join(", ") : "Global"}
                        </Text>
                      );
                    } else if (cell.column.Header === "EVENT") {
                      data = (
                        <Text
                          color={textColor}
                          fontSize="l"
                          fontWeight="700"
                          textAlign={"center"}
                        >
                          {cell.value}
                        </Text>
                      );
                    } else if (cell.column.Header === "DAILY CAP") {
                      data = (
                        <Text
                          color={textColor}
                          fontSize="sm"
                          fontWeight="500"
                          textAlign={"center"}
                        >
                          ${Number(cell.value).toFixed(0)}
                        </Text>
                      );
                    } else if (cell.column.Header === "SPEND") {
                      data = (
                        <Text
                          color={textColor}
                          fontSize="sm"
                          fontWeight="500"
                          textAlign={"center"}
                        >
                          ${Number(cell.value).toFixed(2)}
                        </Text>
                      );
                    } else if (cell.column.Header === "CPI") {
                      data = (
                        <Text
                          color={textColor}
                          fontSize="sm"
                          fontWeight="500"
                          textAlign={"center"}
                        >
                          $
                          {Number(
                            cell.row.values.spend / cell.row.values.installs
                          ).toFixed(2)}
                        </Text>
                      );
                    } else {
                      data = (
                        <Text
                          color={textColor}
                          fontSize="sm"
                          fontWeight="500"
                          textAlign={"center"}
                        >
                          {cell.value}
                        </Text>
                      );
                    }

                    if (isBreakdown) {
                      if (cell.column.id === "breakdowns.user_platform_id") {
                        data = (
                          <Text
                            _hover={{ textDecoration: "underline" }}
                            color={textColor}
                            fontSize="sm"
                            fontWeight="700"
                            textAlign={"center"}
                          >
                            {cell.value === 1 ? <FaAndroid /> : <FaApple />}
                          </Text>
                        );
                      } else {
                        data = (
                          <Text
                            _hover={{ textDecoration: "underline" }}
                            color={textColor}
                            fontSize="sm"
                            fontWeight="700"
                            textAlign={"center"}
                          >
                            {cell.column.id === "breakdowns.user_app_id"
                              ? namesByAppId[cell.value] ?? cell.value
                              : cell.value}
                          </Text>
                        );
                      }
                    }
                    return (
                      <Td
                        {...cell.getCellProps()}
                        key={index}
                        fontSize={{ sm: "14px" }}
                        maxH="30px !important"
                        px={"8px"}
                        py={isSummaryRow ? "16px" : "8px"}
                        minW={{ sm: "80px", md: "120px", lg: "2px" }}
                        borderColor="transparent"
                      >
                        <Skeleton
                          borderRadius={16}
                          endColor={"rgba(53,63,255,0.17)"}
                          isLoaded={!tableLoading}
                        >
                          {data}
                        </Skeleton>
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
        </Tbody>
      </Table>
    </Card>
  );
}

function isDateDifferenceLessThanOrEqual(dateString: string, x: number) {
  // Convert the input date string to a Date object
  let inputDate = new Date(dateString);

  // Get the current UTC date
  let currentDate = new Date();

  // Calculate the time difference in milliseconds
  let timeDifference = currentDate.getTime() - inputDate.getTime();

  // Convert the time difference to days
  let daysDifference = timeDifference / (1000 * 60 * 60 * 24);

  // Check if the difference is less than or equal to x
  return daysDifference <= x;
}

function extractNumberFromString(str: string): number {
  // Use a regular expression to match the numeric part after 'D'
  const match = str.match(/D(\d+)/);

  // Check if a match is found
  if (match && match[1]) {
    // Extract the matched numeric part and convert it to a number
    return parseInt(match[1], 10);
  }

  // Return null if no match is found
  return 0;
}
